import { useResolvedGridAppearance } from "@/components/organisms/Table/Table.tools";
import { Values, ZIndex } from "@/constants";
import { motion } from "framer-motion";
import React from "react";
import { useTreeStore } from "./Tree.store";
import { TreeCell } from "./TreeCell";
import { useShallow } from "zustand/react/shallow";

interface TreeHeaderProps {
  columnsLockedLeft: any[];
  columnsUnlocked: any[];
  columnsLockedRight: any[];
  negativeScrollLeft: any;
  isLoading: boolean;
}

function TreeHeaderComponent({
  columnsLockedLeft,
  columnsUnlocked,
  columnsLockedRight,
  negativeScrollLeft,
  isLoading,
}: TreeHeaderProps) {
  const layout = useTreeStore((state) => state.layout);
  const gridColors = useResolvedGridAppearance(layout?.appearance);
  const [visibleStartColumnIndex, visibleEndColumnIndex] = useTreeStore(
    useShallow((state) => [
      state.visibleStartColumnIndex,
      state.visibleEndColumnIndex,
    ]),
  );

  // Chevron column width matches row height
  const chevronWidth = Values.DEFAULT_ROW_HEIGHT;

  if (!layout) {
    return null;
  }
  return (
    <>
      {/* Header - Locked Left (includes chevron column placeholder) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: ZIndex.GRID.HEADER_LOCKED,
        }}
      >
        {/* Chevron column header - empty placeholder */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: chevronWidth,
            height: Values.DEFAULT_ROW_HEIGHT,
            backgroundColor: gridColors.headerBackground,
            borderRight: "1px solid var(--border)",
            borderBottom: "1px solid var(--border)",
            boxSizing: "border-box",
            zIndex: ZIndex.GRID.HEADER_FIXED_ELEMENT,
          }}
        />
        {columnsLockedLeft.map((col, index) => (
          <TreeCell
            key={index + col.id}
            col={{
              ...col,
              zIndex: ZIndex.GRID.HEADER_LOCKED - index,
            }}
            nodeId={null}
            data={null}
            isHeader={true}
            isSticky={false}
            isLoading={isLoading}
            isLastColumn={false}
          />
        ))}
      </div>

      {/* Header - Scrollable */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          x: negativeScrollLeft,
          zIndex: ZIndex.GRID.HEADER_SCROLLABLE,
        }}
      >
        {columnsUnlocked
          .slice(visibleStartColumnIndex, visibleEndColumnIndex + 1)
          .map((col, index) => (
            <TreeCell
              key={index + col.id}
              col={{
                ...col,
                zIndex: ZIndex.GRID.HEADER_SCROLLABLE - index,
              }}
              nodeId={null}
              data={null}
              isHeader={true}
              isSticky={false}
              isLoading={isLoading}
              isLastColumn={
                columnsLockedRight.length === 0 &&
                index === columnsUnlocked.length - 1
              }
            />
          ))}
      </motion.div>

      {/* Header - Locked Right */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: ZIndex.GRID.HEADER_LOCKED,
        }}
      >
        {columnsLockedRight.map((col, index) => (
          <TreeCell
            key={index + col.id}
            col={{
              ...col,
              zIndex: ZIndex.GRID.HEADER_LOCKED - index,
            }}
            nodeId={null}
            data={null}
            isHeader={true}
            isSticky={false}
            isLoading={isLoading}
            isLastColumn={index === columnsLockedRight.length - 1}
          />
        ))}
      </div>
    </>
  );
}

// Custom comparison function for React.memo
const arePropsEqual = (
  prevProps: TreeHeaderProps,
  nextProps: TreeHeaderProps,
) => {
  // Compare column arrays (shallow comparison of lengths and first few items)
  if (
    prevProps.columnsLockedLeft.length !== nextProps.columnsLockedLeft.length ||
    prevProps.columnsUnlocked.length !== nextProps.columnsUnlocked.length ||
    prevProps.columnsLockedRight.length !== nextProps.columnsLockedRight.length
  ) {
    return false;
  }

  // Compare first column in each array to detect changes
  if (
    prevProps.columnsLockedLeft.length > 0 &&
    nextProps.columnsLockedLeft.length > 0
  ) {
    if (
      prevProps.columnsLockedLeft[0].id !== nextProps.columnsLockedLeft[0].id ||
      prevProps.columnsLockedLeft[0].label !==
        nextProps.columnsLockedLeft[0].label ||
      prevProps.columnsLockedLeft[0].width !==
        nextProps.columnsLockedLeft[0].width
    ) {
      return false;
    }
  }

  if (
    prevProps.columnsUnlocked.length > 0 &&
    nextProps.columnsUnlocked.length > 0
  ) {
    if (
      prevProps.columnsUnlocked[0].id !== nextProps.columnsUnlocked[0].id ||
      prevProps.columnsUnlocked[0].label !==
        nextProps.columnsUnlocked[0].label ||
      prevProps.columnsUnlocked[0].width !== nextProps.columnsUnlocked[0].width
    ) {
      return false;
    }
  }

  if (prevProps.isLoading !== nextProps.isLoading) {
    return false;
  }

  return true;
};

export const TreeHeader = React.memo(TreeHeaderComponent, arePropsEqual);
